import ButtonSize from '@/Enums/ButtonSize.enum';
import type { Channel } from '@/Enums/Channel.enum';
import Color from '@/Enums/Color.enum';
import InventoryType from '@/Enums/InventoryType.enum';
import type { IRadioField } from '@/Interfaces/Forecaster/Forecast.interface';
import type { IProduct } from '@/Interfaces/Product.interface';
import type { IPublisher } from '@/Interfaces/Publisher.interface';
import { DownloadButton } from '@/components/Buttons/DownloadButton';
import PackageDeliveredIcon from '@/components/Icons/PackageDeliveredIcon';
import SearchIcon from '@/components/Icons/SearchIcon';
import SupportRequestModal from '@/components/Modal/SupportRequestModal';
import ProductDomainList from '@/components/ProductDomainList';
import ProductInventoryEmptyState from '@/components/ProductInventoryEmptyState';
import ProductInventoryFooter from '@/components/ProductInventoryFooter';
import ProductInventorySearch from '@/components/ProductInventorySearch';
import ProductInventoryTabs from '@/components/ProductInventoryTabs';
import IOSSwitch from '@/components/Switch';
import StyledInput from '@/components/TabSelect/styled/StyledInput';
import { getProductControlName } from '@/constants/forecaster';
import { downloadTableCsv } from '@/helpers/downloadCSV';
import { getCurrentChannelProducts } from '@/helpers/forecaster';
import { filterByName } from '@/helpers/productInventory';
import useInventoryTabs from '@/hooks/useInventoryTabs';
import useListSearch from '@/hooks/useListSearch';
import useProductDomains from '@/hooks/useProductDomains';
import { useAppSelector } from '@/store/hooks';
import { Button, FormLabel, Paper, Radio, RadioGroup } from '@mui/material';
import type { MouseEvent } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import PackageItem from './PackageItem';
import PublisherSection from './PublisherSection';

interface IProps {
  currentChannel: Channel;
}

const handleRadioClick = (field: IRadioField) => (e: MouseEvent<HTMLElement>) => {
  const value = (e.target as HTMLInputElement).value;
  if (value && value === String(field.value)) field.onChange(false);
};

const DefaultProductSelection = ({ currentChannel }: IProps) => {
  const { control, setValue } = useFormContext();
  const products = useAppSelector(state => state.forecaster.products);
  const forecastByPublishers = useAppSelector(state => state.currentWhitelabel.whitelabel.forecastByPub);
  const isRequestProductButtonHidden = useAppSelector(
    state => state.currentWhitelabel.whitelabel.hideRequestProductButton,
  );

  const productControlName = useMemo(() => getProductControlName(currentChannel), [currentChannel]);
  const customListControlName = useMemo(() => `${currentChannel}.isCustomList`, [currentChannel]);

  const currentProduct = useWatch({ control, name: productControlName });
  const isCustomList = useWatch({ control, name: customListControlName });

  const currentChannelProducts = useMemo(
    () => getCurrentChannelProducts(products, currentChannel),
    [currentChannel, products],
  );
  const selectedProduct = useMemo(
    () => currentChannelProducts?.find((product: IProduct) => product.id === +currentProduct),
    [currentChannelProducts, currentProduct],
  );
  const currentProductPublishers = useMemo(() => selectedProduct?.publishers ?? [], [selectedProduct]);
  const nameBase = useMemo(() => `${productControlName}_${currentProduct}_pubs`, [currentProduct, productControlName]);

  // Only products from /products/by-channels carry hasDomains, so Direct Audio and custom products never fetch here.
  const hasDomains = !!selectedProduct?.hasDomains;
  const { domains, isLoading: areDomainsLoading } = useProductDomains(hasDomains ? selectedProduct.id : null);
  const { tabs, activeTab, setActiveTab } = useInventoryTabs({
    productId: selectedProduct?.id,
    publishersCount: currentProductPublishers.length,
    hasDomains,
    domainsCount: domains.length || selectedProduct?.domainsCount,
  });

  const {
    search: inventorySearch,
    searchTerm: inventorySearchTerm,
    onSearchValueChange: onInventorySearchChange,
    clearSearch: clearInventorySearch,
  } = useListSearch();
  useEffect(() => {
    clearInventorySearch();
  }, [activeTab, selectedProduct?.id, clearInventorySearch]);

  const isDomainsTab = activeTab === InventoryType.Domains;
  const displayedPublishers = useMemo(
    () => filterByName(currentProductPublishers, inventorySearchTerm),
    [currentProductPublishers, inventorySearchTerm],
  );
  const displayedDomains = useMemo(() => filterByName(domains, inventorySearchTerm), [domains, inventorySearchTerm]);
  const activeInventoryCount = isDomainsTab ? domains.length : currentProductPublishers.length;
  const displayedInventoryCount = isDomainsTab ? displayedDomains.length : displayedPublishers.length;
  const inventoryLabel = isDomainsTab ? 'domains' : 'publishers';

  const onClearProduct = useCallback(
    () => setValue(productControlName, false, { shouldDirty: true }),
    [setValue, productControlName],
  );

  const onDownload = useCallback(() => {
    const currentPackage = selectedProduct;
    if (!currentPackage) return;
    const hasBoth = !!currentProductPublishers.length && !!domains.length;
    const formattedData = [
      ...currentProductPublishers.map((publisher: IPublisher) => ({
        package: currentPackage.name,
        publisher: publisher.name,
        ...(hasBoth && { domain: '' }),
      })),
      ...domains.map(domain => ({
        package: currentPackage.name,
        ...(hasBoth && { publisher: '' }),
        domain: domain.name,
      })),
    ];
    downloadTableCsv(formattedData, 'Product Selection');
  }, [selectedProduct, currentProductPublishers, domains]);

  const [isRequestProductsModalOpen, setIsRequestProductsModalOpen] = useState(false);

  const { search, searchTerm, onSearchValueChange } = useListSearch();

  const displayedProducts = useMemo(() => {
    if (!searchTerm.length) return currentChannelProducts;
    return currentChannelProducts.filter((p: IProduct) => p.name?.toLowerCase().includes(searchTerm));
  }, [currentChannelProducts, searchTerm]);

  return (
    <Paper className='col-span-3 p-4 print:p-[10px] break-after-page'>
      <div className='flex flex-col gap-3'>
        <div className='flex items-center justify-between'>
          <div className='flex items-center'>
            <PackageDeliveredIcon />
            <p className='ml-2 text-[#2E2C34] text-[18px] font-semibold'>Product Selection</p>
          </div>
          <div className='flex items-center space-x-2'>
            {forecastByPublishers && (
              <>
                <FormLabel htmlFor='custom-list' sx={{ margin: 0 }}>
                  Custom List
                </FormLabel>
                <Controller
                  control={control}
                  name={customListControlName}
                  render={({ field }) => <IOSSwitch id='custom-list' {...field} checked={field.value} />}
                />
              </>
            )}
            {!isRequestProductButtonHidden && (
              <Button onClick={() => setIsRequestProductsModalOpen(true)} variant='contained'>
                Request Products
              </Button>
            )}
            <DownloadButton
              size={ButtonSize.Medium}
              disabled={!currentProduct || areDomainsLoading}
              onClick={onDownload}
            />
          </div>
        </div>
        <div className='flex flex-col border border-[#E8E8E8] rounded-md'>
          <div className='w-full flex bg-[#E8E8E8]/40 rounded-t-lg'>
            <div
              className={`flex-1 flex items-center justify-between gap-3 px-4 py-2 border-r font-[Manrope] text-[14px]`}
            >
              <span className={`text-[${Color.Grey}] font-semibold`}>Available Packages</span>
              {selectedProduct && (
                <span className='flex items-center gap-3'>
                  <span className={`font-semibold text-[${Color.Primary}]`}>1 selected</span>
                  <Button
                    variant='text'
                    onClick={onClearProduct}
                    sx={{ p: 0, minWidth: 0, fontSize: '14px', textDecoration: 'underline', color: Color.Grey }}
                  >
                    Clear
                  </Button>
                </span>
              )}
            </div>
            <ProductInventoryTabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} className='flex-1 px-4' />
          </div>
          <div className='flex max-h-72'>
            <Controller
              name={productControlName}
              control={control}
              defaultValue={false}
              render={({ field }) => (
                <RadioGroup
                  {...field}
                  onClick={handleRadioClick(field)}
                  sx={{ flex: 1, padding: '8px', overflowY: 'auto', flexWrap: 'nowrap' }}
                  className='border-r'
                >
                  <div className='flex gap-2 mb-1'>
                    <StyledInput
                      fullWidth
                      startAdornment={<SearchIcon className={`text-[${Color.Grey}]`} />}
                      placeholder='Type to search'
                      value={search}
                      onChange={onSearchValueChange}
                      sx={{ padding: '0 7px 0 16px' }}
                    />
                  </div>
                  {displayedProducts.map((product: IProduct) => (
                    <Radio
                      sx={{ width: '100%', padding: 0 }}
                      value={product.id}
                      key={product.id}
                      checkedIcon={<PackageItem {...product} checked />}
                      icon={<PackageItem {...product} />}
                      disableRipple
                    />
                  ))}
                </RadioGroup>
              )}
            />
            <div className='flex-1 flex flex-col min-h-0'>
              {!selectedProduct || !tabs.length ? (
                <ProductInventoryEmptyState productName={selectedProduct?.name} className='flex-1' />
              ) : (
                <>
                  <div className='flex-1 flex flex-col min-h-0 p-2 gap-1'>
                    {!!activeInventoryCount && (
                      <ProductInventorySearch
                        label={inventoryLabel}
                        total={activeInventoryCount}
                        search={inventorySearch}
                        onChange={onInventorySearchChange}
                        onClear={clearInventorySearch}
                        hasNoMatch={!displayedInventoryCount}
                      />
                    )}
                    <div className='flex-1 min-h-0 overflow-y-auto'>
                      {isDomainsTab ? (
                        <ProductDomainList domains={displayedDomains} />
                      ) : (
                        <PublisherSection
                          publishers={displayedPublishers}
                          isCustomList={isCustomList}
                          nameBase={nameBase}
                        />
                      )}
                    </div>
                  </div>
                  {!!activeInventoryCount && (
                    <ProductInventoryFooter
                      label={inventoryLabel}
                      total={activeInventoryCount}
                      shown={displayedInventoryCount}
                      productName={selectedProduct.name}
                    />
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      <SupportRequestModal
        title='Products'
        isOpen={isRequestProductsModalOpen}
        handleClose={() => setIsRequestProductsModalOpen(false)}
      />
    </Paper>
  );
};

export default DefaultProductSelection;
